import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import Space from '@/components/common/Space';

export interface FieldProps {
    name: string;
    id: string;
    label?: ReactNode;
    description?: ReactNode;
    helpElement?: ReactNode;
    errorElement?: ReactNode;
    labelExtra?: ReactNode;
    children: ReactNode;
    className?: string;
    required?: boolean;
    /** Font size for label and description. Defaults to 'base' (16px) */
    labelSize?: 'sm' | 'base';
    /** Font weight for label. Defaults to 'semibold' */
    labelWeight?: 'medium' | 'semibold';
    /** Whether to capitalize the label. Defaults to true */
    labelCapitalize?: boolean;
    disabled?: boolean;
    showGap?: boolean;
    /** Display label and input inline. Defaults to false */
    inline?: boolean;
}

const Field = ({
    name,
    id,
    label,
    description,
    labelExtra,
    helpElement,
    errorElement,
    children,
    className,
    required,
    labelSize = 'base',
    labelWeight = 'semibold',
    labelCapitalize = true,
    disabled,
    showGap = true,
    inline = false,
}: FieldProps) => {
    let labelElement: ReactNode = null;
    const labelTextSize = labelSize === 'base' ? 'text-base' : 'text-sm';
    const labelFontWeight = labelWeight === 'semibold' ? 'font-semibold' : 'font-medium';

    if (label) {
        labelElement = (
            <>
                <label
                    className={clsx(
                        'block text-left leading-6 text-neutral-800 dark:text-white',
                        labelTextSize,
                        labelFontWeight
                    )}
                    htmlFor={id}
                >
                    <span className={labelCapitalize ? 'capitalize' : ''}>{label}</span>
                    {required && <span className="ml-1 text-red-500">*</span>}
                </label>
                {description && (
                    <>
                        <span className={clsx('font-medium text-neutral-500 dark:text-white', labelTextSize)}>
                            {description}
                        </span>
                    </>
                )}
            </>
        );

        if (labelExtra) {
            labelElement = (
                <div className="flex items-center justify-between">
                    {labelElement}
                    {labelExtra}
                </div>
            );
        }
    }

    if (inline) {
        return (
            <Space
                className={clsx(className, 'w-full')}
                direction="horizontal"
                gapSize={5}
                data-input-name={name}
                aria-disabled={disabled || undefined}
            >
                <div className="shrink-0">{labelElement}</div>
                <div className="flex-1">{children}</div>
            </Space>
        );
    }

    return (
        <Space
            className={clsx(className, 'w-full')}
            direction="vertical"
            gapSize={showGap ? 1.5 : 0}
            data-input-name={name}
            aria-disabled={disabled || undefined}
        >
            {labelElement}
            {children}
            {errorElement && <p className="text-base text-red-500">{errorElement}</p>}
            {helpElement && <p className="text-base font-medium text-neutral-500 dark:text-white">{helpElement}</p>}
        </Space>
    );
};

export default Field;
